import React, {useState} from 'react';
import {AdminUserAlert, PageTitle, TableLikeText} from "../_components";
import {Link, useParams} from "react-router-dom";
import {useAuth} from "../../context";
import {useGetJson, useInterval, usePostJson} from "../../hooks";
import {capitalizeFirstLetter, endpoints, minToVerboseTime, repeatType, timeUTCMinToLocalString} from "../../utils2";
import {Alert, Badge, Button, Col, InputGroup, OverlayTrigger, Popover, Row, Stack,} from "react-bootstrap";
import {EventType1, EventType2, ProjectMap, RelayAction} from "../../utils2/maps";
import {isoTimestampToReadableDateOnly} from "../backend2/admin/utilities/readableDate";
import {useSSE} from "../../hooks/useSSE";
import {DisplayRssi} from "./icons/DisplayRssi";
import {WhiteCard} from "./_components/WhiteCard";
import {RenderDailyShort} from "./_components/UsefulComponents";
import {ABOverlayTooltipClick} from "../_components/CustomOverlayTooltip";

export function DashboardDevice() {
    const {uid, did} = useParams()
    const {authToken} = useAuth()

    const [dev, loadingDevice, errorDevice, clearErrorDevice] = useGetJson(endpoints.device.get(uid, did), authToken?.jwt)
    const device = dev?.data

    const fields = ["online", "dev", "rtcc", "net", "wifi", "rssi", "next", "relay"]
    const [update, updateLoading, updateError, clearUpdateError] = useSSE(endpoints.push.device(uid, did, authToken.jwt, fields), fields)

    const [sendPush, value, loadingPush, errorPush, clearErrorPush] = usePostJson(endpoints.push.relay(uid, did), authToken?.jwt)

    function handleRelayClick(idx, state) {
        sendPush({idx, state})
    }

    return <>
        <AdminUserAlert/>
        <Alert variant="danger" show={errorDevice} dismissible onClose={clearErrorDevice}>{errorDevice}</Alert>
        <Alert variant="danger" show={updateError} dismissible onClose={clearUpdateError}>{updateError}</Alert>
        <Alert variant="danger" show={errorPush} dismissible onClose={clearErrorPush}>{errorPush}</Alert>

        <PageTitle title={device?.name}>{`Manage ${device?.device_project}`}</PageTitle>

        {update?.online ? <>
                <div className="d-flex flex-wrap justify-content-between gap-2">
                    <WhiteCard className="flex-fill">
                        {update && <RtcCard online={update.online} rtcc={update.rtcc}/>}
                    </WhiteCard>
                    <WhiteCard className="flex-fill">
                        <NetConnection update={update}/>
                    </WhiteCard>
                    <WhiteCard className="flex-fill">
                        <NextCard next={update?.next}/>
                    </WhiteCard>
                </div>

                <LiveControl device={device} relay={update?.relay} online={update?.online} handleRelayClick={handleRelayClick}/>

                <Row>
                    <Col>
                        <NetworkCard net={update?.net}/>
                    </Col>
                    <Col>
                        <DeviceCard dev={update?.dev} device={device}/>
                    </Col>
                </Row>


            </>
            : <Alert variant="danger">{device?.name} is offline</Alert>
        }

        <Stack direction="horizontal" gap="2" className="py-3 justify-content-end">
            <Button as={Link} variant="link" to={`/device/${uid}`}>Go back</Button>
            <Button as={Link} variant="primary" to={`/device/${uid}/${did}/config`}>Configure</Button>
        </Stack>

    </>
}

function RtcCard({online, rtcc}) {

    const [runtime, setRuntime] = useState()
    const [dynDate, setDynDate] = useState()

    useInterval(() => {
        if (online) {
            const diff = Number(rtcc?.date) - Number(rtcc?.tstamp)
            setRuntime(prev => prev ? prev + 1 : diff)
            setDynDate(prev => prev ? prev + 1 : rtcc?.date)
        } else {
            setRuntime(null)
            setDynDate(null)
        }
    }, 1000, [online, rtcc])

    return <div>
        <div className="d-flex align-items-center gap-2 mb-2">
            <Badge bg="success">
                <div className="d-flex align-items-center gap-2">ONLINE <i
                    className={online ? "fs-5 bi bi-cloud" : "fs-5 bi bi-cloud-slash"}/></div>
            </Badge>
            <ABOverlayTooltipClick placement="bottom" tip="Uptime">{online && minToVerboseTime(runtime / 60)}</ABOverlayTooltipClick>
        </div>
        <div><ABOverlayTooltipClick tip="RTC" placement="bottom">{online ? new Date(Number(dynDate) * 1000).toLocaleString() : "Offline"}</ABOverlayTooltipClick></div>
        <div><ABOverlayTooltipClick tip="NTP" placement="bottom">{rtcc?.NTP || "Undef."}</ABOverlayTooltipClick></div>

    </div>
}

function NetConnection({update}) {
    const AuthMap = ["Undef", "OPEN", "WEP", "WPA/WPA2", "WPA2", "WPA2/WPA3", "WPA3", "WPA/WPA2 Enterprise", "WPA2 Enterprise", "WPA2/WPA3 Enterprise", "WPA3 Enterprise"]

    return <div>
        <div className="mb-2"><Badge bg="info">
            <div className="d-flex align-items-center gap-2 mb-">CONNECTED <DisplayRssi rssi={update?.rssi?.rssi} lock={update?.wifi?.auth}/></div>
        </Badge></div>
        <ABOverlayTooltipClick placement="bottom" tip={<>
            <div>RSSI: {update?.rssi?.rssi}</div>
            <div>AUTH: {AuthMap[update?.wifi?.auth]}</div>
            <div>Channel: {update?.wifi?.ch}</div>
        </>}>
            <span>{update?.wifi?.ssid}</span>
        </ABOverlayTooltipClick>
        <div>{<a href={"http://" + update?.net?.ip} target="_blank"
                 className="link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover"
        >{update?.net?.ip}</a>}</div>

    </div>
}

function NetworkCard({net}) {

    if (net)
        return <WhiteCard>
            <OverlayTrigger trigger="click" placement="right" overlay={<Popover id="popover-basic">
                <Popover.Header as="h3">Additional details</Popover.Header>
                <Popover.Body>
                    <div>
                        <TableLikeText title="IPv4 address" value={net.ip}/>
                        <TableLikeText title="IPv4 gateway" value={net?.gw}/>
                        <TableLikeText title="IPv4 broadcast" value={net?.bcast}/>
                        <TableLikeText title="IPv4 mask" value={net?.mask}/>
                        <TableLikeText title="DNS1" value={net?.dns1}/>
                        <TableLikeText title="DNS2" value={net?.dns2}/>
                        <TableLikeText title="Manufacturer" value={net?.mnf}/>
                        <TableLikeText title="Driver" value={net?.drv}/>
                    </div>
                </Popover.Body>
            </Popover>}>
                <Button variant="div" className="p-0">
                    <div className="fw-semibold text-body-secondary text-capitalize">Network</div>
                </Button>
            </OverlayTrigger>

            <TableLikeText title="IP Assignment" value={net?.dhcp ? "DHCP" : "Manual"}/>
            <TableLikeText title="MAC" value={net?.mac.toUpperCase()}/>
            <TableLikeText title="NetBIOS" value={net?.bios}/>
            <TableLikeText title="Interface" value={net?.name}/>
        </WhiteCard>
    else
        return null
}

function DeviceCard({dev, device}) {
    const {admin} = useAuth()

    if (dev)
        return <WhiteCard>
            <OverlayTrigger trigger="click" placement="right" overlay={<Popover id="popover-basic">
                <Popover.Header as="h3">Additional details</Popover.Header>
                <Popover.Body>
                    <div>
                        <TableLikeText title="Project code" value={dev.prjCode}/>
                        <TableLikeText title="Hardware version" value={dev.hard}/>
                        <TableLikeText title="Build date" value={dev.date}/>
                        <TableLikeText title="Build time" value={dev.time}/>
                        <TableLikeText title="Device ID" value={dev.devid.toString(16)}/>
                        <TableLikeText title="Device VER" value={dev.devver}/>
                        <TableLikeText title="USER ID" value={dev.userid}/>
                        {admin && device?.updated_at && <TableLikeText title="Updated" value={isoTimestampToReadableDateOnly(device?.updated_at)}/>}
                    </div>
                </Popover.Body>
            </Popover>}>
                <Button variant="div" className="p-0">
                    <div className="fw-semibold text-body-secondary text-capitalize">Device details</div>
                </Button>
            </OverlayTrigger>
            <TableLikeText title="Project name" value={dev.prjName}/>
            <TableLikeText title="Serial" value={device?.device_serial}/>
            <TableLikeText title="Firmware version" value={dev.firm}/>
            <TableLikeText title="Activated" value={isoTimestampToReadableDateOnly(device?.created_at)}/>
        </WhiteCard>
    else
        return null
}

// const NextCard = memo(function NextCard({next}) {
function NextCard({next}) {
    const {uid, did} = useParams()
    const {authToken} = useAuth()

    // const mycall = useCallback(() => useGetJson(endpoints.event.get(next?.uuid), authToken?.jwt), [])
    // const [ev, loadingDevice, errorDevice, clearErrorDevice] = mycall()

    // const uuid = useMemo(() => next?.uuid, [next?.uuid])
    // const url = useCallback(() => endpoints.event.get(uuid), [uuid])

    const [ev, loadingDevice, errorDevice, clearErrorDevice] = useGetJson(endpoints.event.get(next?.uuid), authToken?.jwt)
    const event = ev?.data
    const config1 = event?.config1
    const config2 = event?.config2

    if (next)
        return <>
            <div className="d-flex align-items-center gap-2 mb-2">
                <Badge bg="info">
                    <div className="d-flex align-items-center gap-2">NEXT <i
                        className={event?.event_type1 ? "fs-5 bi bi-bell" : "fs-5 bi bi-bell-slash"}/></div>
                </Badge>
                <div>{event?.event_type1 === EventType1.Automation ? "Automation" : "Controlled-By"}</div>
            </div>
            {event?.event_type1 === EventType1.Automation && (
                <Link to={`/device/${uid}/${did}/time/${next?.uuid}`}
                      className="link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover">
                    <div className="d-flex">
                        {config1.repeat === repeatType.Daily ? (
                            <RenderDailyShort config1={config1}/>
                        ) : (
                            <div>{`${capitalizeFirstLetter(config1.weekly_start.slice(0, 3))} - ${capitalizeFirstLetter(config1.weekly_stop.slice(0, 3))}`}</div>
                        )}
                        <div className="me-1">;</div>
                        <div>
                            {`${timeUTCMinToLocalString(config1?.time_start)} - ${timeUTCMinToLocalString(config1?.time_stop)}`}
                        </div>
                    </div>
                </Link>
            )}
            {event?.event_type2 === EventType2.Relay &&
                <Link to={`/device/${uid}/${did}/relay1/${next?.uuid}`}
                      className="link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover">
                    <span className="">{RelayAction[config2?.action].name}</span></Link>}
        </>
    else
        return null
}

function LiveControl({device, relay, online, handleRelayClick}) {
    // function range(stop) {
    //     return Array.from({length: stop}, (_, i) => i);
    // }

    const project = ProjectMap.find(i => i.project === device?.device_project)

    if (project)
        return <WhiteCard>

            {project.configure.map((conf, idx) =>
                <div key={idx} className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <Badge bg={relay?.states[idx] ? "success" : "danger"}>
                            <div className="d-flex align-items-center gap-1">{relay?.states[idx] ? "ON" : "OFF"} <i
                                className={relay?.states[idx] ? "fs-5 bi bi-lightbulb" : "fs-5 bi bi-lightbulb-off"}/></div>
                        </Badge>
                        <div>Relay {idx + 1}</div>
                    </div>
                    <div>
                        <InputGroup>
                            <Button variant="success" disabled={!online}
                                    onClick={() => handleRelayClick(idx, true)}>Turn On</Button>
                            <Button variant="danger" disabled={!online}
                                    onClick={() => handleRelayClick(idx, false)}>Turn Off</Button>
                        </InputGroup>
                    </div>


                </div>
            )}
        </WhiteCard>
    else
        return null
}

